import type { StreamSlot } from "../types/stream";

/**
 * Port of Android `StoredCast` / `OwnCastStore` (com.project.streaming): the casts this tab
 * started and has not seen end. Only these may be stopped as orphans by `reconcile` — a cast
 * the same account runs in another tab, on a phone, or a later cast in the same slot is never
 * this tab's to stop. Lives in sessionStorage (per tab, survives a reload), never localStorage:
 * a second tab must not see the first tab's casts. Cleared on logout.
 */
export interface StoredCast {
  slotId: number;
  userId: string | null;
  streamId: string | null;
  mediaId: number | null;
  /** Wall clock; the last time this tab kept the cast alive (start or a ping that went through). */
  aliveAtMs: number;
}

export const OWN_CAST_KEY = "own_vmpl_casts";
/** 3 missed pings (3 x PING_INTERVAL 30 s): later, the server has freed the slot anyway. */
export const STORED_CAST_TTL_MS = 90_000;

/** Android `StoredCast.of`. */
export function storedCastOf(slot: StreamSlot, nowMs: number): StoredCast {
  return {
    slotId: slot.id,
    userId: slot.streamingUser?.userId ?? null,
    streamId: slot.streamId ?? null,
    mediaId: slot.mediaId ?? null,
    aliveAtMs: nowMs,
  };
}

/**
 * Android `StoredCast.isSameCast`: `slot` is this very cast, published by `me` — not a later one
 * in the same slot, nor another user's. Missing fields compare as null (Kotlin's `==`).
 */
export function isSameCast(e: StoredCast, slot: StreamSlot, me: string): boolean {
  return (
    slot.id === e.slotId &&
    slot.available === false &&
    e.userId === me &&
    slot.streamingUser?.userId === me &&
    (slot.streamId ?? null) === e.streamId &&
    (slot.mediaId ?? null) === e.mediaId
  );
}

const isEntry = (x: unknown): x is StoredCast =>
  typeof x === "object" && x !== null && typeof (x as { slotId?: unknown }).slotId === "number";

function read(): StoredCast[] {
  try {
    const raw = sessionStorage.getItem(OWN_CAST_KEY);
    if (raw == null) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isEntry) : [];
  } catch {
    return [];
  }
}

function write(entries: StoredCast[]): void {
  try {
    sessionStorage.setItem(OWN_CAST_KEY, JSON.stringify(entries));
  } catch {
    // Blocked storage: no orphan record this session, nothing else breaks.
  }
}

export const ownCastRecord = {
  entries: (): StoredCast[] => read(),
  put(c: StoredCast): void {
    write([...read().filter((x) => x.slotId !== c.slotId), c]);
  },
  remove(slotId: number): void {
    write(read().filter((x) => x.slotId !== slotId));
  },
  clear(): void {
    try {
      sessionStorage.removeItem(OWN_CAST_KEY);
    } catch {
      // Blocked storage: nothing stored to clear.
    }
  },
};
